import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { AppAccess, Disclose, Loading, freshToken } from "@lopie/kernel-ui";

import { AccessImpact, useAccessImpact } from "./AccessImpact.jsx";
import SandboxInputs from "./SandboxInputs.jsx";
import {
  CallLog,
  EgressLog,
  FOCUS_RING,
  Group,
  GuardedButton,
  HELD,
  MONO,
  Mark,
  OPEN,
  RecoveryNotice,
  Said,
  Steps,
  useLog,
  useSandbox,
} from "./SandboxPanel.jsx";
import { useNotices } from "./SandboxUpkeep.jsx";
import { ALLOWED_BASIS, AllowedList, LiveLinks, RUNNING, consoleUrl, money } from "./parts.jsx";
import { impactOf } from "./impact.js";
import { awaitingReply, fingerprint, pollDelay } from "./poll.js";
import {
  DOESNT_SHOW,
  JOURNEY_WORD,
  TOPIC_WORD,
  ago,
  allowedLines,
  appTitle,
  copyHelp,
  copyLine,
  fixRequest,
  journeyOf,
  liveRunNote,
  nextStepOf,
  reviewersLine,
  settingsLine,
  signInLine,
  sendWhy,
  situationOf,
  statusOf,
  testUsersOf,
  topicsOf,
  verdictOf,
} from "./plain.js";
import { REVISION_STEP_WORD, previousBuildLine, revisionOf } from "./revision.js";
import {
  STEP_WORD,
  egressReading,
  groupsOf,
  headline,
  runOf,
  steps,
  submitGate,
  viewerUrl,
  when,
} from "./sandboxPanel.js";
import { activityLine, estimateRows, noticeSwitch } from "./upkeep.js";

/**
 * One app's page (LO-207), for the person who asked for it.
 *
 * What it answers, in this order: where is my app, what do I do next, did it work, and what is
 * it allowed to do. A header says where it is in one sentence and holds the three things an
 * author does (open it, ask for a change, send it for approval); the main column is the app
 * tried out, how it did, and what it asks for; a side column that stays in view is the next
 * step, the box to ask for a change, and what the test copy costs. A reviewer's detail is not
 * gone: it is under "For reviewers" and "Test settings", each with a line that is true while it
 * is closed.
 *
 * Everything drawn is what the kernel and the control plane already said, read the way the
 * sandbox panel reads it (`useSandbox`, `useLog`) and worded by `plain.js`. Nothing here decides
 * whether a run passed: `headline` and `submitGate` read the kernel's own answer, and the page
 * says it in plain words beside it. A refusal is shown in the kernel's sentence with its status
 * and code, never as a bare "unavailable".
 */

/** Two columns from here up; below it, one column and the next step in a bar that stays in view. */
const WIDE = "@media (min-width:1000px)";

const TONE_COLOR = (tone) => (theme) => {
  const palette = (theme.vars || theme).palette;
  return tone && tone !== "default" && palette[tone] ? palette[tone].main : palette.text.secondary;
};
/** The glyph's own colour, mixed toward the text colour so it reads in both schemes. */
const TONE_TEXT = (tone) => (theme) => {
  const palette = (theme.vars || theme).palette;
  return tone && tone !== "default" && palette[tone]
    ? `color-mix(in srgb, ${palette[tone].main} 60%, ${palette.text.primary})`
    : palette.text.secondary;
};

/** Read aloud and not seen. Pixels, not `width: 1`, which MUI reads as 100%. */
const SR_ONLY = {
  position: "absolute", width: "1px", height: "1px", margin: "-1px", padding: 0, overflow: "hidden",
  clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
};

/** A glyph in a ring. Never read aloud: the sentence beside it says the same in words. */
function Marker({ glyph, tone, size = 28 }) {
  return (
    <Box component="span" aria-hidden sx={{
      flex: "none", display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: "50%", border: 2, borderColor: TONE_COLOR(tone),
      color: TONE_TEXT(tone), fontWeight: 700, fontSize: `${Math.round(size * 0.5)}px`, lineHeight: 1,
    }}>
      {glyph}
    </Box>
  );
}

/** A titled section. Its heading names it for a screen reader as well as for the eye. */
function Card({ title, aside, children, dense, sx }) {
  const id = useId();
  return (
    <Paper variant="outlined" component="section" aria-labelledby={id} sx={{ p: dense ? 1.75 : 2.5, minWidth: 0, ...sx }}>
      <Stack spacing={1.5}>
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography id={id} component="h2" variant="h2">{title}</Typography>
          {aside}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

/** The time, kept fresh: "10 minutes ago" is worth nothing once it has stopped being true. */
function useNow(every = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), every);
    return () => clearInterval(timer);
  }, [every]);
  return now;
}

/**
 * The newest chat's own view, read only while it is at work: a revision is a chat that has been
 * told something since the draft was saved, and the chat is the only thing that knows (LO-206).
 * Read once, then at the pace the chat page uses while the builder owes a reply, and not again
 * once it does not.
 */
function useChatView(get, id) {
  const [view, setView] = useState(null);
  // False from the moment there is a chat to read until the first look at it has an answer, of either
  // kind: until then the page cannot say whether the draft in front of it is the one being made.
  const [ready, setReady] = useState(!id);
  useEffect(() => {
    setView(null);
    setReady(!id);
    if (!id) return undefined;
    let live = true;
    let timer;
    let unchanged = 0;
    let last = "";
    let waiting = true;
    const schedule = () => {
      clearTimeout(timer);
      if (!waiting) return;
      const delay = pollDelay({ working: true, hidden: document.hidden, unchanged });
      if (delay !== null) timer = setTimeout(look, delay);
    };
    async function look() {
      let next = null;
      try {
        next = await get(`/builder/sessions/${id}`);
      } catch {
        /* the page says what it knows without it */
      }
      if (!live) return;
      if (next) setView(next);
      setReady(true);
      waiting = Boolean(next) && awaitingReply(next);
      const seen = fingerprint(next);
      unchanged = next && seen === last ? unchanged + 1 : 0;
      last = seen;
      schedule();
    }
    const onVisibility = () => {
      if (document.hidden) clearTimeout(timer);
      else if (waiting) { unchanged = 0; clearTimeout(timer); look(); }
    };
    document.addEventListener("visibilitychange", onVisibility);
    look();
    return () => {
      live = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [get, id]);
  return { view, ready };
}

/** Opening the test copy, with the viewer's own token on the fragment (LO-188). The tab is opened
 *  on the click, before the awaits, or the browser blocks it as a pop-up; its opener is cut before
 *  it leaves, because what it is about to show is code nobody has approved. */
function useOpenTestCopy(appUrl) {
  const [opening, setOpening] = useState(false);
  const [failed, setFailed] = useState("");
  const open = async () => {
    setOpening(true);
    setFailed("");
    const tab = window.open("about:blank", "_blank");
    try {
      if (!tab) throw new Error("Your browser blocked the new tab. Allow pop-ups for Builder and try again.");
      const href = viewerUrl(appUrl, await freshToken());
      if (!href) throw new Error("There is no address to open, or no sign-in to open it with.");
      try { tab.opener = null; } catch { /* already cut */ }
      tab.location.href = href;
    } catch (failure) {
      if (tab) tab.close();
      setFailed(failure.message);
    } finally {
      setOpening(false);
    }
  };
  return { open, opening, failed };
}

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// -- the header ------------------------------------------------------------------------------

/** Five steps and where the app is on them. A step says where it is in a glyph and in a word that
 *  is read aloud: done, now, not yet, needs attention. */
function Journey({ list }) {
  const GLYPH = { done: "✓", current: "●", todo: "○", stuck: "!" };
  return (
    <Box component="ol" aria-label="Progress" sx={{ display: "flex", gap: 0.75, m: 0, p: 0, listStyle: "none" }}>
      {list.map((s) => (
        <Box component="li" key={s.key} aria-current={s.state === "current" ? "step" : undefined}
          sx={{ flex: 1, minWidth: 0 }}>
          <Box aria-hidden sx={{
            height: 5, borderRadius: 3, mb: 0.75,
            bgcolor: (theme) => {
              const p = (theme.vars || theme).palette;
              return s.state === "done" ? p.primary.main
                : s.state === "current" ? `color-mix(in srgb, ${p.primary.main} 55%, ${p.divider})`
                  : s.state === "stuck" ? p.warning.main : p.divider;
            },
          }} />
          <Typography variant="caption" sx={{
            display: "block", lineHeight: 1.3, fontSize: "0.6875rem", [WIDE]: { fontSize: "0.75rem" },
            fontWeight: s.state === "current" || s.state === "stuck" ? 700 : 500,
            color: s.state === "todo" ? "text.secondary" : "text.primary",
          }}>
            <Box component="span" aria-hidden sx={{ display: "block", [WIDE]: { display: "inline", mr: 0.5 } }}>{GLYPH[s.state]}</Box>
            {s.label}
            <Box component="span" sx={SR_ONLY}>{` (${JOURNEY_WORD[s.state]})`}</Box>
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/** What an author does, always where it can be reached: open it, ask for a change, send it. Send for
 *  approval is not pressable until it can be sent, says why in words beside it, and stays where the
 *  keyboard can find it so the reason can be reached too. */
function Actions({ open, canOpen, opening, onAsk, send, why, whyId, openWhyId }) {
  return (
    <Stack sx={{ display: "none", [WIDE]: { display: "flex" }, alignItems: "flex-end", gap: 0.75, flex: "none", maxWidth: 520 }}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", justifyContent: "flex-end", rowGap: 1 }}>
        <GuardedButton can={canOpen && !opening} reasonId={openWhyId} onClick={open} variant="outlined" size="medium">
          {opening ? "Opening…" : "Open the app"}
        </GuardedButton>
        <Button variant="outlined" onClick={onAsk}>Ask for a change</Button>
        <GuardedButton can={send.can} reasonId={whyId} onClick={send.run} size="medium">
          {send.busy ? "Sending…" : "Send for approval"}
        </GuardedButton>
      </Stack>
      {why ? (
        <Typography id={whyId} variant="caption" color="text.secondary" sx={{ textAlign: "right", ...HELD }}>{why}</Typography>
      ) : null}
    </Stack>
  );
}

// -- next step -------------------------------------------------------------------------------

/** The next thing, its button, and when it cannot be done yet, why. Drawn twice, as a card beside the
 *  page on a wide screen and as a bar that stays in view on a narrow one; only one is ever shown. */
function NextStep({ step, run, compact, secondary, note }) {
  const whyId = useId();
  if (!step) return null;
  const action = step.action;
  return (
    <Stack spacing={compact ? 1 : 1.25} sx={{ minWidth: 0 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{step.title}</Typography>
        <Typography id={step.why ? whyId : undefined} variant={compact ? "caption" : "body2"} color="text.secondary" sx={{
          display: "block", ...HELD,
          ...(compact ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" } : {}),
        }}>
          {step.why || step.text}
        </Typography>
        {note ? (
          <Typography variant="body2" sx={{ display: "block", mt: 0.75, ...HELD }}>{note}</Typography>
        ) : null}
      </Box>
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        {action ? (
          step.action.kind === "submit" ? (
            <GuardedButton can={step.can} reasonId={whyId} onClick={run} size="medium"
              sx={compact ? { flex: 1 } : undefined}>
              {action.label}
            </GuardedButton>
          ) : (
            <Button variant="contained" onClick={run} sx={compact ? { flex: 1 } : undefined}>{action.label}</Button>
          )
        ) : null}
        {secondary}
      </Stack>
    </Stack>
  );
}

// -- how it did in testing -------------------------------------------------------------------

/** The kernel's check as a row: a glyph and a word, the topic in words, and when it did not pass or
 *  could not be checked, why. Never colour alone. */
function TopicRow({ row, onFix }) {
  const GLYPH = { passed: "✓", failed: "✕", unjudged: "!", untested: "–" };
  const TONE = { passed: "success", failed: "error", unjudged: "warning", untested: "default" };
  return (
    <Box component="li" sx={{ listStyle: "none", py: 0.75, minWidth: 0 }}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
        <Marker glyph={GLYPH[row.status]} tone={TONE[row.status]} size={20} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "baseline", columnGap: 1.5, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, ...HELD }}>{row.label}</Typography>
            <Typography variant="body2" sx={{ color: TONE_TEXT(TONE[row.status]), fontWeight: row.status === "failed" ? 700 : 600, flex: "none" }}>
              {TOPIC_WORD[row.status]}
            </Typography>
          </Stack>
          {row.detail ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ...HELD }}>{row.detail}</Typography>
          ) : null}
          {row.fixes.length ? (
            <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.25 }}>
              {row.fixes.map((f) => (
                <Typography key={`${f.id}:${f.where}`} component="li" variant="caption" sx={{ ...HELD }}>
                  {f.title} <Box component="span" sx={{ color: "text.secondary" }}>({f.where})</Box>
                </Typography>
              ))}
            </Box>
          ) : null}
          {row.status === "failed" ? (
            <Button size="small" sx={{ mt: 0.5, ml: -1 }} onClick={() => onFix(row)}>Ask the builder to fix this</Button>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

/** The three steps a test copy goes through, in words an author knows. */
const COPY_STEP = { instance: "Building the test copy", seed: "Adding made-up data", qa: "Running the checks" };

function CopySteps({ list }) {
  const GLYPH = { done: "✓", active: "↻", waiting: "…", failed: "✕" };
  const TONE = { done: "success", active: "info", waiting: "default", failed: "error" };
  return (
    <Box component="ol" aria-label="Test copy progress" sx={{ display: "grid", gap: 0.75, m: 0, p: 0, listStyle: "none" }}>
      {list.map((s) => (
        <Stack component="li" direction="row" key={s.key} spacing={1.25} sx={{ alignItems: "center" }}>
          <Marker glyph={GLYPH[s.state]} tone={TONE[s.state]} size={20} />
          <Typography variant="body2">
            {COPY_STEP[s.key] || s.label}{" "}
            <Box component="span" sx={{ color: "text.secondary" }}>{STEP_WORD[s.state]}</Box>
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

const REVISION_LABEL = { working: "Builder working", build: "Build", check: "Code check", saved: "Draft saved", sandbox: "New test copy" };
const REVISION_GLYPH = { done: "✓", active: "↻", waiting: "…", skipped: "–" };
const REVISION_TONE = { done: "success", active: "info", waiting: "default", skipped: "default" };

/** What a revision is doing: the steps the builder takes to the next draft, each in a word. */
function RevisionProgress({ revision }) {
  return (
    <Box component="ol" aria-label="The new version" sx={{ display: "grid", gap: 0.75, m: 0, p: 0, listStyle: "none" }}>
      {revision.steps.map((s) => (
        <Stack component="li" direction="row" key={s.key} spacing={1.25} sx={{ alignItems: "center" }}>
          <Marker glyph={REVISION_GLYPH[s.state]} tone={REVISION_TONE[s.state]} size={20} />
          <Typography variant="body2">
            {REVISION_LABEL[s.key] || s.label}{" "}
            <Box component="span" sx={{ color: "text.secondary" }}>{REVISION_STEP_WORD[s.state]}</Box>
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

// -- the folded detail -----------------------------------------------------------------------

/** A heading that opens its detail, with a line under it that is true while it is closed. Opened in
 *  place and kept once opened, so what somebody was in the middle of editing is not lost by closing it. */
function Detail({ label, summary, children }) {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(false);
  const id = useId();
  return (
    <Box sx={{ minWidth: 0 }}>
      <Link component="button" type="button" underline="hover" aria-expanded={open} aria-controls={id}
        onClick={() => { setOpen((was) => !was); setSeen(true); }}
        sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75, fontWeight: 600, textAlign: "left", typography: "subtitle2" }}>
        {label}
        <Box component="span" aria-hidden sx={{
          fontSize: "0.7em", transform: open ? "rotate(90deg)" : "none", transition: "transform 120ms",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}>▶</Box>
      </Link>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, ...HELD }}>{summary}</Typography>
      <Collapse in={open} id={id}>
        <Box sx={{ pt: 1.5 }}>{seen ? children : null}</Box>
      </Collapse>
    </Box>
  );
}

// -- the page --------------------------------------------------------------------------------

/** One chat in the list under the box to ask for a change: its title, what it cost in small text, and
 *  the one thing you can do to the list entry. */
function ChatLine({ chat, onOpen, onArchive }) {
  const working = RUNNING.includes(chat.status);
  const small = [
    chat.cost_cents ? money(chat.cost_cents) : "",
    working ? "working" : "",
    chat.template ? `copy of ${chat.template}` : "",
  ].filter(Boolean).join(" · ");
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", minWidth: 0 }}>
      <Link component="button" type="button" onClick={() => onOpen(chat.id)}
        sx={{ flex: 1, minWidth: 0, textAlign: "left", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {chat.title || chat.id}
      </Link>
      {small ? <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{small}</Typography> : null}
      <Button size="small" onClick={() => onArchive(chat.id, true)} aria-label={`Archive ${chat.title || chat.id}`}
        sx={{ minWidth: 0, px: 0.75 }}>
        Archive
      </Button>
    </Stack>
  );
}

function AppView({ app, onBack, onOpenSession, onArchive, onChange, onReplaced, busy, get, post }) {
  const listedId = app.request?.request_id || "";
  const data = useSandbox(get, listedId);
  const now = useNow();
  const head = data.head || listedId;
  // The request is kept while the next one is read: a draft that is replaced (the author saves new
  // test settings) is read again from the start, and the page, and what somebody is typing into it,
  // stays where it is rather than going and coming back.
  const lastRequest = useRef(null);
  if (data.request) lastRequest.current = data.request;
  const request = data.request || (data.loading ? lastRequest.current : null);
  const { box, qa, qaFailure, again } = data;
  const run = runOf(qa);
  const view = useMemo(() => headline(qa), [qa]);

  // A revision is the builder at work on what comes after the draft: the chat is read only while a chat
  // about this app is at work, and only a draft has a revision to be past.
  const state = request?.state || app.request?.state;
  // How many people could not open this draft as it asks (LO-211): read for a draft, before it is sent.
  const impact = useAccessImpact(get, head, state === "draft" && Boolean(request));
  const atWork = app.sessions.find((s) => RUNNING.includes(s.status));
  const { view: chat, ready: chatRead } = useChatView(get, state === "draft" && atWork ? atWork.id : "");
  const revision = useMemo(() => revisionOf({ chat, request, asked: null, savesAtMount: null }), [chat, request]);
  const working = chat ? awaitingReply(chat) : Boolean(atWork);

  // The builder saves the new draft for a revision: the chat's own view names it, and the list is read
  // again so the page is for it. Once per draft named, so a chat that names one the list does not
  // have cannot ask again and again.
  const named = useRef("");
  const chatDraft = chat?.request_id || "";
  useEffect(() => {
    if (chatDraft && chatDraft !== head && chatDraft !== named.current) {
      named.current = chatDraft;
      onReplaced?.();
    }
  }, [chatDraft, head, onReplaced]);

  const gate = submitGate({ request, qa, qaFailure, box, revision });
  const instances = app.instances || [];
  const liveInstance = instances.find((i) => i.url && i.status === "ready") || null;
  const users = testUsersOf(request, run);
  const situation = situationOf({
    loading: (Boolean(listedId) && data.loading) || !chatRead,
    unreadable: Boolean(data.requestFailure) && !request,
    state: listedId ? state : undefined,
    liveReady: Boolean(liveInstance),
    liveVersion: app.live_version || 0,
    // A sandbox the control plane could not be asked about is not a sandbox that does not exist.
    box: box?.state || (data.controlFailure ? "unknown" : "none"),
    qaUnreadable: Boolean(qaFailure) && !run,
    tone: view.tone,
    revising: Boolean(revision?.working),
    working,
  });
  const verdict = verdictOf({ qa, users: users.length, now });
  const status = statusOf(situation, {
    version: app.live_version, ago: verdict.when, sent: ago(request?.requested_at, now),
    reason: request?.reason || app.request?.reason || "", box: box?.state || "none",
    recovery: box?.recovery?.state || "",
  });
  const draftOpen = Boolean(listedId) && OPEN.includes(state);
  const latest = app.sessions[0] || null;
  // Not before the chat has been read: a revision in progress would otherwise be sendable for as long as
  // the first look takes.
  const canSend = gate.can && situation !== "loading";
  const plainWhy = sendWhy(situation, gate, box);
  const step = nextStepOf(situation, { can: canSend, why: plainWhy, hasChat: Boolean(atWork || latest) });
  // Said beside Send for approval when some people could not open it as it asks. It tells and does not hold:
  // the button is as available as it was, and the whole sentence is under what the app asks to be allowed to do.
  const impactView = impact.phase === "ready" ? impactOf(impact.body) : null;
  const sendNote = impactView?.kind === "blocked" && step?.action?.kind === "submit"
    ? `${impactView.blocked} of ${impactView.total} ${impactView.total === 1 ? "person" : "people"} in your org couldn't open it as requested. This doesn't stop you sending it; the detail is under What it asks to be allowed to do.`
    : "";

  // -- what the person can do
  const [text, setText] = useState("");
  const changeRef = useRef(null);
  const askChange = useCallback(() => {
    const el = changeRef.current;
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: reducedMotion() ? "auto" : "smooth" });
    el.focus({ preventScroll: true });
  }, []);
  const fix = (row) => { setText(fixRequest(row)); askChange(); };

  const copy = useOpenTestCopy(box?.appUrl);
  const openLive = liveInstance?.url || "";
  const canOpen = Boolean(box?.appUrl) || (!draftOpen && Boolean(openLive));
  const openApp = () => {
    if (box?.appUrl) copy.open();
    else if (openLive) window.open(openLive, "_blank", "noopener,noreferrer");
  };

  const [submitting, setSubmitting] = useState(false);
  const [refused, setRefused] = useState(null);
  const [sent, setSent] = useState(false);
  const alertRef = useRef(null);
  const submit = async () => {
    setSubmitting(true);
    setRefused(null);
    try {
      await post(`/manifest-requests/${head}/submit`, {});
      setSent(true);
      onReplaced?.();
    } catch (failure) {
      setRefused(failure);
      alertRef.current?.scrollIntoView({ block: "center", behavior: reducedMotion() ? "auto" : "smooth" });
    } finally {
      setSubmitting(false);
      again();
    }
  };
  const [starting, setStarting] = useState(false);
  const [startRefused, setStartRefused] = useState(null);
  const startRun = async () => {
    setStarting(true);
    setStartRefused(null);
    try {
      await post(`/sandboxes/${head}/qa`, {});
    } catch (failure) {
      setStartRefused(failure);
    } finally {
      setStarting(false);
      again();
    }
  };

  const doStep = () => {
    switch (step?.action?.kind) {
      case "submit": return submit();
      case "run": return startRun();
      case "ask": return askChange();
      case "refresh": return again();
      case "reload": return onReplaced?.();
      case "continue": return latest && onOpenSession(latest.id);
      case "chat": return (atWork || latest) && onOpenSession((atWork || latest).id);
      case "requests": return window.open(`${consoleUrl()}/#requests`, "_blank", "noopener,noreferrer");
      default: return undefined;
    }
  };

  // -- the logs, read when a run has an outcome, for the reviewers' line
  const finished = run && run.status !== "running" ? `${run.run_id}:${run.status}` : "";
  const calls = useLog(get, `/sandboxes/${head}/calls`, draftOpen ? finished : "");
  const egress = useLog(get, `/sandboxes/${head}/egress`, draftOpen ? finished : "");

  // -- the pieces
  const rows = useMemo(() => topicsOf(run), [run]);
  const list = useMemo(() => steps(box, qa), [box, qa]);
  const reasonId = useId();
  const statusId = useId();
  const reviewers = useMemo(() => reviewersLine({
    users: users.length, calls: calls.body, egress: egress.body, reading: egressReading(egress.body),
  }), [users.length, calls.body, egress.body]);
  const upkeep = request?.sandbox_upkeep;
  const copyInfo = copyLine(upkeep);
  const notices = useNotices(get, post, Boolean(copyInfo) && draftOpen);
  const sw = noticeSwitch(notices.prefs, "sandbox");
  const [help, setHelp] = useState(false);
  const helpId = useId();
  const [allChats, setAllChats] = useState(false);

  const notReady = situation === "box_wait" || situation === "box_failed" || situation === "box_stale" || situation === "box_unknown";
  const revising = situation === "revising";
  const title = appTitle(app.app);
  const journey = journeyOf(situation);
  const chatsShown = allChats ? app.sessions : app.sessions.slice(0, 2);
  const sendProps = { can: canSend && !submitting, run: submit, busy: submitting };

  const result = (
    <Stack spacing={1.5} ref={alertRef}>
      {sent && request?.state !== "pending" ? <Alert severity="success" variant="outlined" role="status">Sent for approval.</Alert> : null}
      {refused ? <Said failure={refused} title="It wasn't sent: the Kernel refused this" /> : null}
      {copy.failed ? <Alert severity="warning" variant="outlined" role="alert">{copy.failed}</Alert> : null}
    </Stack>
  );

  const tryIt = (
    <Card title={draftOpen || !liveInstance ? "Try it yourself" : "Use it"}>
      {draftOpen || !liveInstance ? (
        box?.appUrl ? (
          <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
            <Button variant="contained" size="large" onClick={copy.open} disabled={copy.opening}>
              {copy.opening ? "Opening…" : "Open the app"}
            </Button>
            <Typography variant="body2" sx={HELD}>{signInLine(users)}</Typography>
            {users.length ? (
              <Stack component="ul" aria-label="Test users" direction="row" sx={{ gap: 1, flexWrap: "wrap", m: 0, p: 0, listStyle: "none" }}>
                {users.map((u) => <Box component="li" key={u}><Chip size="small" variant="outlined" label={u} /></Box>)}
              </Stack>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={HELD}>
              {revising ? "This is the previous version's test copy. " : ""}It opens in a new tab and says on every screen that it is a test copy.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={HELD}>
              {situation === "none" || situation === "building" || situation === "unreadable" || situation === "loading"
                ? "There is nothing to open yet."
                : "It can't be opened yet. The link appears here when the test copy is ready."}
            </Typography>
            {draftOpen && box ? <CopySteps list={list} /> : null}
          </Stack>
        )
      ) : null}
      {liveInstance && !draftOpen ? (
        <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
          {instances.length === 1 ? (
            <Button variant="contained" size="large" href={liveInstance.url} target="_blank" rel="noreferrer">Open the app</Button>
          ) : <LiveLinks instances={instances} />}
          <Typography variant="body2" color="text.secondary">
            {app.live_version ? `Version ${app.live_version} is live. ` : ""}It's the real app, with real records.
          </Typography>
        </Stack>
      ) : null}
      {liveInstance && draftOpen ? (
        <>
          <Divider />
          <Stack spacing={0.75}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>The live app{app.live_version ? `, version ${app.live_version}` : ""}</Typography>
            <LiveLinks instances={instances} />
          </Stack>
        </>
      ) : null}
      {!draftOpen && !liveInstance && app.live_version ? <LiveLinks instances={instances} /> : null}
    </Card>
  );

  const previousLine = `Previous version: ${verdict.word.toLowerCase()}${verdict.when ? `, ${verdict.when}` : ""}. That is the previous version's result, not a result for the revision.`;
  const verdictBlock = (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
        <Marker glyph={verdict.glyph} tone={verdict.tone} />
        <Typography variant="body1" sx={{ ...HELD, pt: 0.25 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{verdict.word}:</Box> {verdict.text}
          {verdict.when ? <Box component="span" sx={{ color: "text.secondary" }}> ({verdict.when})</Box> : null}
        </Typography>
      </Stack>
      {run && qa?.does_not_prove?.length ? (
        <Disclose label="What this doesn't show" summary={DOESNT_SHOW} align="row">
          <Stack spacing={0.75}>
            {qa.does_not_prove.map((t, i) => (
              <Typography key={i} variant="body2" color="text.secondary" sx={HELD}>{t}</Typography>
            ))}
          </Stack>
        </Disclose>
      ) : null}
      {liveRunNote(run) ? <Alert severity="info" variant="outlined">{liveRunNote(run)}</Alert> : null}
      {qaFailure && !run ? <Typography variant="body2" color="text.secondary">How the checks went couldn't be read just now.</Typography> : null}
      {rows.length ? (
        <Box component="ul" aria-label="What was checked" sx={{ m: 0, p: 0 }}>
          {rows.map((row) => <TopicRow key={row.key} row={row} onFix={fix} />)}
        </Box>
      ) : null}
      {box?.state === "ready" && draftOpen ? (
        <Stack spacing={0.75} sx={{ alignItems: "flex-start" }}>
          <Button variant="outlined" onClick={startRun} disabled={starting || run?.status === "running"}>
            {starting ? "Starting…" : run ? "Run the checks again" : "Run the checks now"}
          </Button>
          {startRefused ? <Said failure={startRefused} title="The checks were not started" /> : null}
        </Stack>
      ) : null}
    </Stack>
  );

  const testing = draftOpen ? (
    <Card title="How it did in testing">
      {revising && revision ? (
        <Stack spacing={1.5}>
          <Alert severity="info" variant="outlined" role="status">
            <AlertTitle>The results below are for the previous version</AlertTitle>
            <Typography variant="body2" sx={HELD}>
              {revision.cause === "asked"
                ? "A revision was asked for after this draft was saved, and it is being built. "
                : "The builder is making a newer version than the one that was tested. "}
              A new test copy starts when the builder saves the new draft, and this page switches to it.
              Until then nothing here is a test of the revision.
            </Typography>
          </Alert>
          <RevisionProgress revision={revision} />
          <Disclose label="Show the previous version's results" summary={previousLine} align="row">
            {verdictBlock}
          </Disclose>
        </Stack>
      ) : notReady || (!run && box?.state !== "ready") ? (
        <Typography variant="body2" color="text.secondary" sx={HELD}>
          The checks start once the test copy is ready. They look at it as each test user and have to pass before it can be sent for approval.
        </Typography>
      ) : verdictBlock}
    </Card>
  ) : (situation === "live" || situation === "live_changing" || situation === "installing") ? (
    <Card title="How it did in testing">
      <Typography variant="body2" sx={HELD}>
        It was tested before a governor approved it. The test copy goes when a request is decided, and what was found is kept with the approval on the{" "}
        <Link href={`${consoleUrl()}/#requests`} target="_blank" rel="noreferrer">console's Requests screen</Link>.
      </Typography>
    </Card>
  ) : null;

  const allowed = request ? (
    <Card title="What it asks to be allowed to do">
      <AllowedList lines={allowedLines(request.proposed)} basis={ALLOWED_BASIS} />
      <AccessImpact state={impact} onAsk={(wording) => { setText(wording); askChange(); }} />
    </Card>
  ) : null;

  const who = app.live_version ? (
    <Card title="Who can use it">
      <AppAccess appType={app.app} get={get} post={post} />
    </Card>
  ) : null;

  const reviewerBody = (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      {draftOpen && !gate.can ? (
        <Stack spacing={0.5} component="section" aria-label="Why Send for approval is unavailable">
          <Typography variant="subtitle2">Why Send for approval is unavailable</Typography>
          <Typography variant="body2" sx={HELD}>{gate.reason}</Typography>
          {gate.basis ? <Typography variant="caption" color="text.secondary" sx={HELD}>{gate.basis}</Typography> : null}
        </Stack>
      ) : null}
      {request ? (
        <Typography variant="body2" sx={HELD}>
          Request <span style={MONO}>{head}</span> is {request.state === "superseded" ? "replaced" : request.state}.
          {request.requested_at ? ` Saved ${when(request.requested_at)}.` : ""}
        </Typography>
      ) : null}
      {data.requestFailure ? <Said failure={data.requestFailure} title="The request could not be read" /> : null}
      {data.controlFailure ? (
        <Alert severity="warning" variant="outlined">
          The sandbox's build state could not be read just now: {data.controlFailure.body?.detail || data.controlFailure.message || "no answer"}.
          What the kernel knows of it is shown without it.
        </Alert>
      ) : null}
      {draftOpen ? (
        <>
          <Typography variant="body2" sx={HELD}>
            {box ? box.detail : "No sandbox yet. One is made when the builder saves a draft that names a build, and takes ten minutes or so."}
          </Typography>
          {box?.recovery ? <RecoveryNotice recovery={box.recovery} /> : null}
          <Steps list={list} />
          {box?.appUrl ? (
            <Typography variant="caption" color="text.secondary" sx={HELD}>
              The sandbox's address is <span style={MONO}>{box.appUrl}</span>. It opens with the viewer's own token, and shows synthetic data.
            </Typography>
          ) : null}
          {run ? (
            <Typography variant="caption" color="text.secondary" sx={HELD}>
              Run <span style={MONO}>{run.run_id}</span>
              {run.started_at ? `, started ${when(run.started_at)}` : ""}
              {run.finished_at ? `, finished ${when(run.finished_at)}` : ""}.
            </Typography>
          ) : null}
          {revising && revision ? (
            <Typography variant="caption" color="text.secondary" sx={HELD}>
              {previousBuildLine({ draft: revision.draft, box, qa })}
            </Typography>
          ) : null}
          {run?.live_run && run.live_run.ran === false ? (
            <Alert severity="info" variant="outlined">
              The live run was skipped ({String(run.live_run.skipped_reason || "no reason given").replace(/_/g, " ")})
              {run.live_run.detail ? `: ${run.live_run.detail}` : "."} Nothing was explored, which is not a pass.
            </Alert>
          ) : null}
          {qaFailure && !run ? <Said failure={qaFailure} title="The QA result could not be read" /> : null}
          {upkeep ? (
            <Stack spacing={0.5} component="section" aria-label="Age and estimated cost">
              <Typography variant="caption" color="text.secondary" sx={HELD}>{activityLine(upkeep)}</Typography>
              {estimateRows(upkeep.estimate).map((r) => (
                <Typography key={r.key} variant="caption" color="text.secondary" sx={HELD}>{r.label}: {r.figure}. {r.basis}.</Typography>
              ))}
              {(upkeep.estimate?.does_not_prove || []).map((t, i) => (
                <Typography key={`n${i}`} variant="caption" color="text.secondary" sx={HELD}>{t}</Typography>
              ))}
              {sw ? <Typography variant="caption" color="text.secondary" sx={HELD}>{sw.detail}.</Typography> : null}
            </Stack>
          ) : null}
        </>
      ) : null}
      {run ? <ReviewerGroups run={run} get={get} requestId={head} /> : null}
      {draftOpen ? (
        <>
          <Divider />
          <CallLog log={calls} />
          <Divider />
          <EgressLog log={egress} />
        </>
      ) : null}
    </Stack>
  );

  const more = listedId ? (
    <Paper variant="outlined" component="section" aria-label="More detail" sx={{ p: 2.5, minWidth: 0 }}>
      <Stack spacing={2} divider={<Divider flexItem />}>
        <Typography component="h2" variant="h2">More detail</Typography>
        <Detail label="For reviewers" summary={draftOpen ? reviewers : `Request ${head} is ${state}.`}>{reviewerBody}</Detail>
        {draftOpen ? (
          <Detail label="Test settings" summary={settingsLine(request?.proposed)}>
            <SandboxInputs get={get} post={post} requestId={listedId} onReplaced={onReplaced} />
          </Detail>
        ) : null}
      </Stack>
    </Paper>
  ) : null;

  const next = (
    <Card title="Next step" dense>
      <NextStep step={step} run={doStep} note={sendNote} />
    </Card>
  );

  const change = (
    <Card title="Change it" dense>
      <TextField inputRef={changeRef} multiline minRows={2} fullWidth value={text} label="What would you like to change?"
        placeholder="e.g. add a column for the last payment date"
        onChange={(e) => setText(e.target.value)} />
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
        <Button variant="contained" disabled={busy || !text.trim()} onClick={() => onChange(text.trim())}>
          {busy ? "Starting…" : "Start the change"}
        </Button>
        {latest ? <Button onClick={() => onOpenSession(latest.id)}>Continue the last chat</Button> : null}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Built, then sent for approval. It replaces what is live only after a governor approves it.
      </Typography>
      {app.sessions.length ? (
        <Stack spacing={0.25} aria-label="Chats about this app" component="section">
          {chatsShown.map((s) => <ChatLine key={s.id} chat={s} onOpen={onOpenSession} onArchive={onArchive} />)}
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            {app.sessions.length > 2 ? (
              <Link component="button" type="button" variant="caption" onClick={() => setAllChats((v) => !v)}>
                {allChats ? "Show fewer chats" : `Show all ${app.sessions.length} chats`}
              </Link>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {money(app.cost_cents)} so far across {app.sessions.length + (app.archived_count || 0)} {app.sessions.length + (app.archived_count || 0) === 1 ? "chat" : "chats"}.
              {app.archived_count ? ` ${app.archived_count} archived: they are on Your apps, under Archived chats.` : ""}
            </Typography>
          </Stack>
        </Stack>
      ) : null}
    </Card>
  );

  const testCopy = copyInfo && draftOpen ? (
    <Card title="Test copy" dense aside={(
      <Button size="small" variant="outlined" aria-label="How the cost and reminders work" aria-expanded={help} aria-controls={helpId}
        onClick={() => setHelp((v) => !v)} sx={{ minWidth: 28, width: 28, height: 28, p: 0, borderRadius: "50%" }}>
        ?
      </Button>
    )}>
      <Typography variant="body2" title={copyInfo.title} sx={HELD}>{copyInfo.text}</Typography>
      {sw ? (
        <FormControlLabel
          control={<Switch size="small" checked={sw.on} disabled={notices.saving} onChange={(e) => notices.flip(e.target.checked)} />}
          label={<Typography variant="body2">{sw.on ? "Emailing you about this test copy: on" : "Emailing you about this test copy: off"}</Typography>}
        />
      ) : null}
      {notices.failure ? (
        <Alert severity="info" variant="outlined">
          Your email setting could not be read or changed: {notices.failure.body?.detail || notices.failure.message || "no answer"}.
        </Alert>
      ) : null}
      <Collapse in={help} id={helpId}>
        <Stack spacing={0.75}>
          {copyHelp(upkeep).map((t) => <Typography key={t} variant="caption" color="text.secondary" sx={HELD}>{t}</Typography>)}
        </Stack>
      </Collapse>
    </Card>
  ) : null;

  const unreadable = app.unreadable?.length && !instances.length;

  return (
    <Box sx={{ maxWidth: 1500, mx: "auto", px: 2, pt: 2, pb: 16, [WIDE]: { px: 4, pt: 3, pb: 5 }, ...FOCUS_RING }}>
      <Paper variant="outlined" component="section" aria-label="This app" sx={{ p: 2.5, [WIDE]: { p: 3 } }}>
        <Stack spacing={1.75}>
          <Link component="button" type="button" onClick={onBack} sx={{ alignSelf: "flex-start" }}>← Your apps</Link>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, [WIDE]: { flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between", gap: 3 } }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}>
                <Typography component="h1" variant="h1" sx={{ fontSize: "1.75rem", lineHeight: 1.2, ...HELD }}>{title}</Typography>
                {app.live_version ? <Mark tone="success" label={`live v${app.live_version}`} /> : null}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, ...MONO, ...HELD }}>{app.app}</Typography>
              {app.derived_from ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ...HELD }}>
                  Copied from {app.derived_from.app}{app.derived_from.version ? ` ${app.derived_from.version}` : ""}.
                  It is a separate app with its own data.
                  {app.derived_from.latest && app.derived_from.version && app.derived_from.latest !== app.derived_from.version
                    ? ` ${app.derived_from.app} is now at ${app.derived_from.latest}; this copy does not have what changed since.`
                    : ""}
                </Typography>
              ) : null}
            </Box>
            <Actions open={openApp} canOpen={canOpen} opening={copy.opening} onAsk={askChange} send={sendProps}
              why={canSend ? "" : plainWhy} whyId={reasonId} openWhyId={statusId} />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", columnGap: 1.25, rowGap: 0.5 }}>
            <Marker glyph={status.glyph} tone={status.tone} />
            <Typography id={statusId} role="status" sx={{ flex: "1 1 0", minWidth: "min(100%, 16rem)", pt: 0.25, fontSize: "1.0625rem", fontWeight: 500, ...HELD }}>
              {status.text}
            </Typography>
            <Box sx={{ flex: "0 0 calc(100% - 40px)", ml: "40px", [WIDE]: { flex: "none", ml: 0 } }}>
              <Button size="small" onClick={() => { again(); }}>Check again</Button>
            </Box>
          </Box>
          {data.quiet ? (
            <Typography variant="caption" color="text.secondary">
              This page stopped refreshing because nothing has changed for a while. Check again looks now.
            </Typography>
          ) : null}
          {unreadable ? (
            <Typography variant="body2" color="text.secondary">
              Whether it is live could not be read just now. Reload to try again.
            </Typography>
          ) : null}
          <Journey list={journey} />
        </Stack>
      </Paper>

      {sent || refused || copy.failed ? <Box sx={{ mt: 2 }}>{result}</Box> : null}

      <Box sx={{
        display: "grid", gap: 2, mt: 2, gridTemplateColumns: "minmax(0, 1fr)",
        [WIDE]: { gridTemplateColumns: "minmax(0, 2fr) minmax(300px, 1fr)", gap: 3, alignItems: "start" },
      }}>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          {tryIt}
          {testing}
          {allowed}
          {who}
          {more}
        </Stack>
        <Stack spacing={1.5} sx={{
          minWidth: 0,
          [WIDE]: { position: "sticky", top: 84, maxHeight: "calc(100vh - 100px)", overflowY: "auto", px: "4px", mx: "-4px", py: "2px", my: "-2px" },
        }}>
          <Box sx={{ display: "none", [WIDE]: { display: "block" } }}>{next}</Box>
          {change}
          {testCopy}
        </Stack>
      </Box>

      <Paper component="section" aria-label="Next step" square elevation={0} sx={{
        display: "block", [WIDE]: { display: "none" },
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1000, p: 1.5,
        borderTop: 1, borderColor: "divider", boxShadow: "0 -4px 12px rgba(0,0,0,0.08)", ...FOCUS_RING,
      }}>
        <NextStep step={step} run={doStep} compact
          secondary={step?.action?.kind !== "ask" ? <Button variant="outlined" onClick={askChange}>Ask for a change</Button> : null} />
      </Paper>
    </Box>
  );
}

/** Each test user's results, as the report has them: what failed first, with its steps, and what passed
 *  one click in. These are the reviewer's words and are the report's own. */
function ReviewerGroups({ run, get, requestId }) {
  const groups = useMemo(() => groupsOf(run), [run]);
  return groups.length ? (
    <Stack spacing={1.5}>
      {groups.map((g) => <Group key={g.key} group={g} get={get} requestId={requestId} runId={run.run_id} />)}
    </Stack>
  ) : null;
}

/** One app: where it is, what to do next, did it work, and a box to change it. */
export default function AppPage({ app, loading, degraded, name, onBack, onOpenSession, onArchive, onChange, onReplaced, busy, get, post }) {
  if (!app && loading) return <Loading label="Reading the app" />;
  if (!app) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto", p: 2 }}>
        <Link component="button" onClick={onBack} sx={{ alignSelf: "flex-start" }}>← Your apps</Link>
        {degraded ? (
          <Alert severity="warning" variant="outlined">
            <AlertTitle>Your apps could not be read</AlertTitle>
            {name} may well be there; this page cannot be drawn until the list
            loads. Go back and try again.
          </Alert>
        ) : (
          <Alert severity="info" variant="outlined">
            <AlertTitle>No chats about {name} on your list</AlertTitle>
            An app is listed here through the chats you had about it. If you
            expected it, it may have been built from a different sign-in.
          </Alert>
        )}
      </Stack>
    );
  }
  return (
    <AppView key={app.app} app={app} onBack={onBack} onOpenSession={onOpenSession} onArchive={onArchive} onChange={onChange}
      onReplaced={onReplaced} busy={busy} get={get} post={post} />
  );
}
